import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import { useBalanceContext } from "../../../contexts/BalanceContext";
import { PrimaryButton, SecondaryButton } from "../buttons";

// Every new player receives this Gold Coin grant on signup (real, granted
// server-side). Used as a graceful fallback for the welcome copy while the live
// balance is still loading (or is unavailable for any reason).
const WELCOME_GOLD = 10000;

const formatGoldAmount = (n) =>
  Math.round(Number(n) || 0).toLocaleString("en-US");

/**
 * NewUserSplash - full-screen onboarding shown once per browser (first login)
 * Persistence + dev overrides intentionally mirror TopBanner.jsx
 * - localStorage key set to '1' after first user choice OR X dismiss
 * - URL overrides: ?onboarding=reset or ?onboarding=show (clears localStorage and shows again)
 * - Locks background scroll while visible
 */
const NewUserSplash = ({
  enabled = true,
  storageKey = "onboardingSplashDismissed_v1",
}) => {
  const { isLoggedIn } = useAuth();
  // Live Gold Coin balance (same shared instance the header/sidebar read, no
  // new fetch). Falls back to the signup grant while the balance is loading.
  const { balances } = useBalanceContext();
  const navigate = useNavigate();
  const location = useLocation();

  const [dismissed, setDismissed] = useState(false);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0); // 0: gate, 1..n: slides (we'll use 1-4)
  const [forceShow, setForceShow] = useState(false);
  const [suppressThisLoad, setSuppressThisLoad] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  // Read persisted state
  useEffect(() => {
    try {
      const val = localStorage.getItem(storageKey);
      if (val === "1") setDismissed(true);
    } catch (_) {}
  }, [storageKey]);

  // Override via query param: ?onboarding=reset or ?onboarding=show (works in all envs)
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const mode = params.get("onboarding");
      if (mode === "reset") {
        localStorage.removeItem(storageKey);
        // Do not show immediately; show on next refresh
        setDismissed(true);
        setForceShow(false);
        setSuppressThisLoad(true);
        // Clean the URL param so it doesn't stick around
        try {
          const url = new URL(window.location.href);
          url.searchParams.delete("onboarding");
          window.history.replaceState(
            null,
            "",
            url.pathname + (url.search ? url.search : "") + url.hash,
          );
        } catch (_) {}
      } else if (mode === "show") {
        localStorage.removeItem(storageKey);
        setDismissed(false);
        setForceShow(true);
        setSuppressThisLoad(false);
        // Clean the URL param after forcing show for this load only
        try {
          const url = new URL(window.location.href);
          url.searchParams.delete("onboarding");
          window.history.replaceState(
            null,
            "",
            url.pathname + (url.search ? url.search : "") + url.hash,
          );
        } catch (_) {}
      } else {
        setForceShow(false);
        setSuppressThisLoad(false);
      }
    } catch (_) {}
  }, [location.search, storageKey]);

  // Control visibility: first login-style gate + not dismissed
  // "enabled" can be turned off by parent (e.g., non-lobby / verified users),
  // but a forced show via ?onboarding=show should still override this for testing.
  useEffect(() => {
    const effectiveEnabled = enabled || forceShow;
    const shouldOpen =
      !suppressThisLoad &&
      effectiveEnabled &&
      isLoggedIn &&
      (!dismissed || forceShow);
    setOpen(shouldOpen);
  }, [enabled, isLoggedIn, dismissed, forceShow, suppressThisLoad]);

  // Lock/unlock background scroll while visible (mobile only)
  const isDesktop = useMemo(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(min-width: 768px)").matches;
  }, []);
  useEffect(() => {
    if (!open) return;
    if (isDesktop) return; // don't block desktop interactions with site behind
    const originalOverflow = document.body.style.overflow;
    const originalTouch = document.body.style.touchAction;
    document.body.style.overflow = "hidden";
    document.body.style.touchAction = "none";
    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.touchAction = originalTouch;
    };
  }, [open, isDesktop]);
  // Mark as dismissed for future visits as soon as it opens once (skip while forced)
  useEffect(() => {
    if (!open || forceShow) return;
    try {
      if (localStorage.getItem(storageKey) !== "1") {
        localStorage.setItem(storageKey, "1");
      }
    } catch (_) {}
  }, [open, storageKey, forceShow]);

  const totalSlides = 5;

  const markDismissedForever = () => {
    try {
      localStorage.setItem(storageKey, "1");
    } catch (_) {}
    setForceShow(false);
    setDismissed(true);
    setOpen(false);
  };

  const handleUsedSweeps = () => {
    // Mark dismissed and drop straight into the games lobby so returning-style
    // players can start playing with their free Gold Coins right away.
    markDismissedForever();
    navigate("/");
  };

  const handleImNew = () => {
    // Mark dismissed right away per requirement, then continue onboarding
    try {
      localStorage.setItem(storageKey, "1");
    } catch (_) {}
    setStep(1);
  };

  const next = () => setStep((s) => Math.min(totalSlides - 1, s + 1));
  const prev = () => setStep((s) => Math.max(1, s - 1));
  const goto = (i) => setStep(i);

  const handleBack = () => {
    if (step <= 1) setStep(0);
    else prev();
  };

  // Icons to mimic mobile notifications header
  const BackArrowIcon = ({ className = "w-6 h-6" }) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth="1.5"
      stroke="currentColor"
      className={className}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18"
      />
    </svg>
  );

  const CloseIcon = ({ className = "w-5 h-5" }) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth="1.5"
      stroke="currentColor"
      className={className}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6 18 18 6M6 6l12 12"
      />
    </svg>
  );

  const LeftSlideIcon = ({ className = "w-5 h-5" }) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth="1.5"
      stroke="currentColor"
      className={className}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15.75 19.5 8.25 12l7.5-7.5"
      />
    </svg>
  );

  const RightSlideIcon = ({ className = "w-5 h-5" }) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth="1.5"
      stroke="currentColor"
      className={className}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="m8.25 4.5 7.5 7.5-7.5 7.5"
      />
    </svg>
  );

  const OnboardingSlide = ({ title, subtitle, children, onBack, footer }) => {
    return (
      <div className="flex-1 flex flex-col min-h-0">
        {/* Header bar (mimic mobile notifications header) */}
        <div className="flex items-center p-4 bg-black border-b border-gray-700 -mx-6 -mt-6">
          <button
            type="button"
            onClick={onBack}
            aria-label="Go back"
            className="p-2 text-gray-400 hover:text-white transition-colors cursor-pointer"
          >
            <BackArrowIcon className="w-6 h-6" />
          </button>
          <h3 className="text-xl md:text-2xl font-bold text-white ml-2 flex-1">
            {title}
          </h3>
          <div className="w-6" />
        </div>

        {/* Optional subtitle below header */}
        {subtitle && (
          <div className="pt-3">
            <p className="text-foreground text-base md:text-lg">{subtitle}</p>
          </div>
        )}

        {/* Slide body + optional fixed footer above dots */}
        <div className="mt-4 md:mt-6 flex-1 min-h-0 relative">
          <div
            className={`overflow-y-auto h-full -mx-6 ${footer ? "pb-24" : ""}`}
          >
            <div className="mx-auto max-w-[760px] w-full px-6 flex flex-col items-center">
              {children}
            </div>
          </div>
          {footer && (
            <div className="pointer-events-none absolute inset-x-0 bottom-12 z-20">
              <div className="mx-auto max-w-[760px] w-full pointer-events-auto">
                {footer}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Swipe handlers for mobile on slides >= 1
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const touchLastX = useRef(null);
  const touchLastY = useRef(null);

  const handleTouchStart = (e) => {
    const t = e.touches?.[0];
    if (!t) return;
    touchStartX.current = t.clientX;
    touchStartY.current = t.clientY;
    touchLastX.current = t.clientX;
    touchLastY.current = t.clientY;
  };
  const handleTouchMove = (e) => {
    const t = e.touches?.[0];
    if (!t) return;
    touchLastX.current = t.clientX;
    touchLastY.current = t.clientY;
  };
  const handleTouchEnd = () => {
    if (step < 1) return;
    const dx = (touchLastX.current ?? 0) - (touchStartX.current ?? 0);
    const dy = (touchLastY.current ?? 0) - (touchStartY.current ?? 0);
    const absDx = Math.abs(dx);
    const absDy = Math.abs(dy);
    if (absDx > 50 && absDx > absDy) {
      if (dx < 0) next();
      else prev();
    }
    touchStartX.current = null;
    touchStartY.current = null;
    touchLastX.current = null;
    touchLastY.current = null;
  };
  if (!open) return null;

  // Prefer the live balance; fall back to the signup grant while it loads.
  const goldDisplay = formatGoldAmount(
    balances?.gold > 0 ? balances.gold : WELCOME_GOLD,
  );

  return (
    <div className="fixed inset-0 z-[20000] overflow-hidden h-[100dvh] md:pointer-events-none">
      <div
        className="relative z-10 w-full h-[100dvh] flex items-stretch justify-center md:justify-end px-0 md:px-0"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className="relative w-full h-[100dvh] md:w-[480px] md:max-w-[480px] md:h-[100dvh] bg-card border-0 rounded-none p-6 flex flex-col overflow-hidden pointer-events-auto">
          {/* Ambient brand background accents (same style as maintenance page), scoped to drawer only */}
          <div
            aria-hidden
            className="pointer-events-none absolute -top-40 -right-40 w-[44rem] h-[44rem] rounded-full blur-3xl opacity-20"
            style={{
              background:
                "radial-gradient(circle at 30% 30%, #F5B955 0%, rgba(245,185,85,0.1) 60%)",
            }}
          />
          <div
            aria-hidden
            className="pointer-events-none absolute -bottom-40 -left-40 w-[44rem] h-[44rem] rounded-full blur-3xl opacity-20"
            style={{
              background:
                "radial-gradient(circle at 70% 70%, #F5B955 0%, rgba(245,185,85,0.08) 65%)",
            }}
          />

          {/* Confirm close modal */}
          {confirmCloseOpen && (
            <div
              className="absolute inset-0 z-50 flex items-center justify-center bg-black/60"
              onClick={() => setConfirmCloseOpen(false)}
            >
              <div
                // eslint-disable-next-line no-restricted-syntax -- intentional darker-than-card surface for the close-confirmation modal
                className="bg-[#0b0b0d] border border-border rounded-xl w-[92%] max-w-sm p-5 text-center"
                onClick={(e) => e.stopPropagation()}
              >
                <p className="text-white text-lg font-semibold">
                  Ready to start playing?
                </p>
                <p className="mt-2 text-sm text-foreground">
                  You've got{" "}
                  <span className="font-semibold text-primary">
                    {goldDisplay}
                  </span>{" "}
                  Gold Coins ready to play.
                </p>
                <div className="mt-16">
                  <PrimaryButton
                    onClick={() => {
                      setConfirmCloseOpen(false);
                      markDismissedForever();
                      navigate("/");
                    }}
                    size="large"
                    className="w-full"
                  >
                    Play now
                  </PrimaryButton>
                  <SecondaryButton
                    onClick={() => {
                      setConfirmCloseOpen(false);
                      markDismissedForever();
                      navigate("/store");
                    }}
                    size="large"
                    className="w-full mt-3 md:mt-4"
                  >
                    Go to Store
                  </SecondaryButton>
                  <SecondaryButton
                    onClick={() => {
                      setConfirmCloseOpen(false);
                    }}
                    size="large"
                    className="w-full mt-3 md:mt-4"
                  >
                    Back to the tour!
                  </SecondaryButton>
                  <button
                    onClick={() => {
                      setConfirmCloseOpen(false);
                      markDismissedForever();
                    }}
                    className="mt-20 text-primary hover:underline cursor-pointer text-base"
                  >
                    I want to look around first
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Close (X) button - only on welcome step */}
          {step === 0 && (
            <button
              type="button"
              onClick={() => setConfirmCloseOpen(true)}
              aria-label="Close onboarding"
              className="absolute top-4 left-4 z-20 h-10 w-10 rounded-md flex items-center justify-center bg-transparent border border-border text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-primary cursor-pointer"
            >
              <CloseIcon className="w-5 h-5" />
            </button>
          )}

          {step === 0 && (
            <div className="flex-1 flex flex-col text-center">
              <div className="flex-1 mt-4 md:mt-6 flex flex-col items-center justify-center space-y-5 md:space-y-8">
                <img
                  src="/assets/3d/cluck_stack_05.png"
                  alt="Cluck logo"
                  className="mx-auto w-[88vw] max-w-[360px] max-h-[36vh] object-contain select-none"
                />
                <h2 className="text-3xl md:text-5xl font-bold text-white">
                  Welcome to Cluck!
                </h2>
                <p className="text-lg md:text-xl text-foreground">
                  The #1 US Sweepstakes Casino
                </p>
                {/* Surface the free signup grant up front so new players know
                    they can play immediately, no purchase needed. */}
                <div className="flex items-center gap-3 rounded-2xl border border-primary/40 bg-primary/10 px-4 py-2.5">
                  <img
                    src="/assets/coins/Gold-Coin.webp"
                    alt="Gold Coins"
                    className="h-8 w-8 object-contain select-none"
                  />
                  <p className="text-sm md:text-base text-white text-left">
                    <span className="font-bold text-primary">
                      {goldDisplay}
                    </span>{" "}
                    Gold Coins are waiting. Free to play right now.
                  </p>
                </div>
              </div>
              <div className="onboarding-cta flex flex-col items-center gap-3 md:gap-4">
                {/* Primary first: I'm new */}
                <PrimaryButton
                  onClick={handleImNew}
                  size="large"
                  className="w-full"
                >
                  I'm new, take the tour!
                </PrimaryButton>
                {/* Secondary link-style: I've used sweeps before */}
                <button
                  onClick={handleUsedSweeps}
                  className="text-primary hover:underline cursor-pointer text-base"
                >
                  Skip tour, start playing
                </button>
              </div>
            </div>
          )}

          {step === 1 && (
            <>
              <OnboardingSlide title="How to Play" onBack={handleBack}>
                <div className="w-full">
                  <div className="space-y-8 md:space-y-10">
                    <img
                      src="/assets/3d/game7.png"
                      alt="How to play"
                      className="mx-auto w-[80vw] max-w-[320px] max-h-[32vh] object-contain select-none"
                    />
                    <div className="space-y-1 md:space-y-2">
                      <p className="text-foreground font-bold text-xl md:text-2xl text-left">
                        Play for Free. Win Cash Prizes
                      </p>
                      <p className="text-foreground text-lg md:text-xl text-left">
                        Cluck offers a diverse selection of thousands of
                        sweepstakes games including Slots, Table, Live Dealer,
                        and Instant.
                      </p>
                    </div>
                    <div className="space-y-2 md:space-y-3">
                      <p className="text-white font-semibold text-xl md:text-2xl text-left">
                        Gold Coins
                      </p>
                      <div className="flex items-start gap-4">
                        <img
                          src="/assets/coins/Gold-Coin.webp"
                          alt="Gold Coins"
                          className="h-24 w-24 md:h-28 md:w-28 object-contain"
                        />
                        <ul className="mt-1 space-y-2 md:space-y-3">
                          <li className="flex items-start gap-3">
                            <span className="mt-2 h-2.5 w-2.5 rounded-full bg-primary shrink-0"></span>
                            <p className="text-foreground text-lg md:text-xl">
                              Play just for fun. No cash value.
                            </p>
                          </li>
                          <li className="flex items-start gap-3">
                            <span className="mt-2 h-2.5 w-2.5 rounded-full bg-primary shrink-0"></span>
                            <p className="text-foreground text-lg md:text-xl">
                              Can be bought or claimed free.
                            </p>
                          </li>
                        </ul>
                      </div>
                    </div>
                    <div className="space-y-2 md:space-y-3">
                      <p className="text-white font-semibold text-xl md:text-2xl text-left">
                        Cluck Bucks
                      </p>
                      <div className="flex items-start gap-4">
                        <img
                          src="/assets/coins/Cluck-Coin.webp"
                          alt="Cluck Bucks"
                          className="h-24 w-24 md:h-28 md:w-28 object-contain"
                        />
                        <ul className="mt-1 space-y-2 md:space-y-3">
                          <li className="flex items-start gap-3">
                            <span className="mt-2 h-2.5 w-2.5 rounded-full bg-primary shrink-0"></span>
                            <p className="text-foreground text-lg md:text-xl">
                              Sweepstakes Credits used to play for real cash
                              prizes.
                            </p>
                          </li>
                          <li className="flex items-start gap-3">
                            <span className="mt-2 h-2.5 w-2.5 rounded-full bg-primary shrink-0"></span>
                            <p className="text-foreground text-lg md:text-xl">
                              Win them free on your day one welcome wheel, or
                              get them with a Gold Coin pack, giveaways or mail
                              in entries
                            </p>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Mobile fine print above dots (inside the rectangle) */}
                <div className="md:hidden mt-4">
                  <p className="text-xs text-muted-foreground leading-snug">
                    Cluck Bucks are promotional sweepstakes entries. No purchase
                    necessary. See official rules for free entry method and
                    eligibility. Gold Coins have no cash value.
                  </p>
                </div>
              </OnboardingSlide>

              {/* Dots + desktop arrows */}
              <div className="mt-4 flex items-center justify-center gap-2">
                <button
                  type="button"
                  aria-label="Previous slide"
                  onClick={prev}
                  disabled={step === 1}
                  className="hidden md:inline-flex h-10 w-10 rounded-md items-center justify-center bg-transparent border border-border text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-primary cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <LeftSlideIcon className="w-5 h-5" />
                </button>

                {Array.from(
                  { length: totalSlides - 1 },
                  (_, idx) => idx + 1,
                ).map((i) => (
                  <button
                    key={i}
                    aria-label={`Go to slide ${i + 1}`}
                    onClick={() => goto(i)}
                    className={`${step === i ? "bg-primary" : "bg-secondary"} h-3 w-3 rounded-full cursor-pointer transition-transform duration-150 hover:scale-110`}
                  />
                ))}

                <button
                  type="button"
                  aria-label="Next slide"
                  onClick={next}
                  disabled={step >= totalSlides - 1}
                  className="hidden md:inline-flex h-10 w-10 rounded-md items-center justify-center bg-transparent border border-border text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-primary cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <RightSlideIcon className="w-5 h-5" />
                </button>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <OnboardingSlide title="Daily Bonuses" onBack={handleBack}>
                <div className="w-full">
                  <div className="space-y-8 md:space-y-10">
                    <div className="space-y-2 md:space-y-3">
                      <p className="text-white font-semibold text-xl md:text-2xl text-left">
                        7 Day Bonus Calendars
                      </p>
                      <div className="space-y-3">
                        <img
                          src="/assets/3d/bonuses.png"
                          alt="New Player Calendar"
                          className="mx-auto w-full max-w-[560px] md:max-w-[420px] object-contain select-none"
                        />
                        <ul className="mt-1 space-y-2 md:space-y-3">
                          <li className="flex items-start gap-3">
                            <span className="mt-2 h-2.5 w-2.5 rounded-full bg-primary shrink-0"></span>
                            <p className="text-foreground text-lg md:text-xl">
                              Login each day to claim your daily bonus
                            </p>
                          </li>
                          <li className="flex items-start gap-3">
                            <span className="mt-2 h-2.5 w-2.5 rounded-full bg-primary shrink-0"></span>
                            <p className="text-foreground text-lg md:text-xl">
                              Miss a day and you'll reset your calendar
                            </p>
                          </li>
                          <li className="flex items-start gap-3">
                            <span className="mt-2 h-2.5 w-2.5 rounded-full bg-primary shrink-0"></span>
                            <p className="text-foreground text-lg md:text-xl">
                              Increase your calendar rewards by increasing your
                              VIP level
                            </p>
                          </li>
                        </ul>
                      </div>
                    </div>

                    <div className="space-y-2 md:space-y-3">
                      <p className="text-white font-semibold text-xl md:text-2xl text-left">
                        VIP Daily Rewards
                      </p>
                      <div className="space-y-3">
                        <img
                          src="/assets/3d/vip-daily.PNG"
                          alt="VIP Daily Rewards"
                          className="mx-auto w-full max-w-[560px] md:max-w-[420px] object-contain select-none"
                        />
                        <ul className="mt-1 space-y-2 md:space-y-3">
                          <li className="flex items-start gap-3">
                            <span className="mt-2 h-2.5 w-2.5 rounded-full bg-primary shrink-0"></span>
                            <p className="text-foreground text-lg md:text-xl">
                              Claim additional daily VIP Rewards
                            </p>
                          </li>
                          <li className="flex items-start gap-3">
                            <span className="mt-2 h-2.5 w-2.5 rounded-full bg-primary shrink-0"></span>
                            <p className="text-foreground text-lg md:text-xl">
                              Rewards scale with VIP level
                            </p>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </OnboardingSlide>

              {/* Dots + desktop arrows */}
              <div className="mt-4 flex items-center justify-center gap-2">
                <button
                  type="button"
                  aria-label="Previous slide"
                  onClick={prev}
                  disabled={step === 1}
                  className="hidden md:inline-flex h-10 w-10 rounded-md items-center justify-center bg-transparent border border-border text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-primary cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <LeftSlideIcon className="w-5 h-5" />
                </button>

                {Array.from(
                  { length: totalSlides - 1 },
                  (_, idx) => idx + 1,
                ).map((i) => (
                  <button
                    key={i}
                    aria-label={`Go to slide ${i + 1}`}
                    onClick={() => goto(i)}
                    className={`${step === i ? "bg-primary" : "bg-secondary"} h-3 w-3 rounded-full cursor-pointer transition-transform duration-150 hover:scale-110`}
                  />
                ))}

                <button
                  type="button"
                  aria-label="Next slide"
                  onClick={next}
                  disabled={step >= totalSlides - 1}
                  className="hidden md:inline-flex h-10 w-10 rounded-md items-center justify-center bg-transparent border border-border text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-primary cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <RightSlideIcon className="w-5 h-5" />
                </button>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <OnboardingSlide title="Coin Packages" onBack={handleBack}>
                <div className="mx-auto max-w-[760px] space-y-10 md:space-y-12 mt-8 md:mt-8">
                  <div className="space-y-2 md:space-y-3">
                    <p className="text-white font-semibold text-2xl md:text-4xl text-left">
                      First Purchase Bonuses
                    </p>
                    <img
                      src="/assets/3d/cluck_stack_04.webp"
                      alt="First-time Purchase Boosts"
                      className="mx-auto w-full max-w-[260px] max-h-[14vh] object-contain select-none"
                    />
                    <p className="text-foreground text-lg md:text-xl text-left">
                      Huge bonuses for new players! 50% bonus on your first
                      purchase, 25% bonus on your second!
                    </p>
                  </div>

                  <div className="space-y-2 md:space-y-3">
                    <p className="text-white font-semibold text-2xl md:text-4xl text-left">
                      Weekly Purchases
                    </p>
                    <img
                      src="/assets/3d/cluck_stack_02.webp"
                      alt="Weekly Resets"
                      className="mx-auto w-full max-w-[260px] max-h-[14vh] object-contain select-none"
                    />
                    <p className="text-foreground text-lg md:text-xl text-left">
                      Earn bonuses on your first purchases of each week. These
                      are small bonuses that reset each week!
                    </p>
                  </div>
                </div>
              </OnboardingSlide>

              {/* Dots + desktop arrows */}
              <div className="mt-4 flex items-center justify-center gap-2">
                <button
                  type="button"
                  aria-label="Previous slide"
                  onClick={prev}
                  disabled={step === 1}
                  className="hidden md:inline-flex h-10 w-10 rounded-md items-center justify-center bg-transparent border border-border text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-primary cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <LeftSlideIcon className="w-5 h-5" />
                </button>

                {Array.from(
                  { length: totalSlides - 1 },
                  (_, idx) => idx + 1,
                ).map((i) => (
                  <button
                    key={i}
                    aria-label={`Go to slide ${i + 1}`}
                    onClick={() => goto(i)}
                    className={`${step === i ? "bg-primary" : "bg-secondary"} h-3 w-3 rounded-full cursor-pointer transition-transform duration-150 hover:scale-110`}
                  />
                ))}

                <button
                  type="button"
                  aria-label="Next slide"
                  onClick={next}
                  disabled={step >= totalSlides - 1}
                  className="hidden md:inline-flex h-10 w-10 rounded-md items-center justify-center bg-transparent border border-border text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-primary cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <RightSlideIcon className="w-5 h-5" />
                </button>
              </div>
            </>
          )}

          {step === 4 && (
            <>
              <OnboardingSlide
                title="Start playing now!"
                onBack={handleBack}
                footer={
                  <div className="flex flex-col gap-3">
                    <PrimaryButton
                      onClick={() => {
                        markDismissedForever();
                        navigate("/");
                      }}
                      size="large"
                      className="w-full"
                    >
                      Play now
                    </PrimaryButton>
                    <SecondaryButton
                      onClick={() => {
                        markDismissedForever();
                        navigate("/store");
                      }}
                      size="large"
                      className="w-full"
                    >
                      Go to Store
                    </SecondaryButton>
                  </div>
                }
              >
                <div className="mx-auto max-w-[760px] space-y-3 md:space-y-4 mt-8 md:mt-8">
                  <img
                    src="/assets/3d/wheel-rooster.webp"
                    alt="Start playing"
                    className="mx-auto w-[80vw] max-w-[320px] max-h-[32vh] object-contain select-none"
                  />
                  <p className="text-foreground text-lg md:text-xl text-left">
                    You've already got{" "}
                    <span className="font-bold text-primary">{goldDisplay}</span>{" "}
                    Gold Coins to play with, free right now. Jump into the games
                    and start spinning.
                  </p>
                </div>
              </OnboardingSlide>

              {/* Dots + desktop arrows */}
              <div className="mt-4 flex items-center justify-center gap-2">
                <button
                  type="button"
                  aria-label="Previous slide"
                  onClick={prev}
                  disabled={step === 1}
                  className="hidden md:inline-flex h-10 w-10 rounded-md items-center justify-center bg-transparent border border-border text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-primary cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <LeftSlideIcon className="w-5 h-5" />
                </button>

                {Array.from(
                  { length: totalSlides - 1 },
                  (_, idx) => idx + 1,
                ).map((i) => (
                  <button
                    key={i}
                    aria-label={`Go to slide ${i + 1}`}
                    onClick={() => goto(i)}
                    className={`${step === i ? "bg-primary" : "bg-secondary"} h-3 w-3 rounded-full cursor-pointer transition-transform duration-150 hover:scale-110`}
                  />
                ))}

                <button
                  type="button"
                  aria-label="Next slide"
                  onClick={next}
                  disabled={step >= totalSlides - 1}
                  className="hidden md:inline-flex h-10 w-10 rounded-md items-center justify-center bg-transparent border border-border text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-primary cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <RightSlideIcon className="w-5 h-5" />
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewUserSplash;
